import { Box, Divider, Paper, SxProps, Theme } from "@mui/material";
import { forwardRef, ReactNode } from "react";
import Flex from "./Flex";
import InfoLabel from "./InfoLabel";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "./app/provider/patient/chart";

export type FormSectionProps = {
  /** Anchor id — pair it with `useScrollSpy` to drive quick-jump navigation. */
  id?: string;
  title: string;
  info?: ReactNode;
  /** Rendered on the right of the header, e.g. a status toggle. */
  action?: ReactNode;
  /**
   * `card` keeps the bordered card with a filled header band.
   * `plain` drops the chrome: a heading over the fields, ruled off below.
   */
  variant?: FormSectionVariant;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export type FormSectionVariant = "card" | "plain";

/**
 * Titled, anchorable group of form fields.
 *
 * @author Rajkumar Rathod
 */
const FormSection = forwardRef<HTMLDivElement, FormSectionProps>(
  ({ id, title, info, action, variant = "card", children, sx }, ref) => {
    if (variant === "plain") {
      return (
        <Box
          ref={ref}
          id={id}
          component="section"
          aria-label={title}
          sx={{ scrollMarginTop: "16px", ...(sx as object) }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mb={2}
          >
            <InfoLabel
              label={title}
              info={info}
              sx={{
                "& p": {
                  ...CHART_TYPOGRAPHY.body3,
                  fontWeight: CHART_TYPOGRAPHY.weight.semiBold,
                  color: CHART_COLORS.textDarker,
                },
              }}
            />
            {action}
          </Flex>

          {children}

          <Divider sx={{ mt: 3, borderColor: CHART_COLORS.borderLight }} />
        </Box>
      );
    }

    return (
      <Paper
        ref={ref}
        id={id}
        component="section"
        elevation={0}
        aria-label={title}
        sx={{
          border: `1px solid ${CHART_COLORS.border}`,
          borderRadius: CHART_DIMENSIONS.borderRadius,
          /** Clips the filled header band to the card's rounded corners. */
          overflow: "hidden",
          bgcolor: CHART_COLORS.surfaceWhite,
          scrollMarginTop: "16px",
          ...(sx as object),
        }}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{
            px: CHART_DIMENSIONS.cardHeaderPxLarge,
            py: CHART_DIMENSIONS.cardHeaderPy,
            borderBottom: `1px solid ${CHART_COLORS.border}`,
            bgcolor: CHART_COLORS.surfaceHeader,
          }}
        >
          <InfoLabel
            label={title}
            info={info}
            sx={{
              "& p": {
                ...CHART_TYPOGRAPHY.title6,
                color: CHART_COLORS.textDarker,
              },
            }}
          />
          {action}
        </Flex>

        <Box sx={{ p: CHART_DIMENSIONS.cardHeaderPxLarge }}>{children}</Box>
      </Paper>
    );
  }
);

FormSection.displayName = "FormSection";

export default FormSection;
